import { Message } from "@buzzeasy/shared-frontend-utilities/components";
import { GlobalToken, Typography } from "antd";
import styled from "styled-components";
import { CallTranslatorMode, IntermediateTranscriptItem, Participant, TranscriptHistoryItem } from "../../model/hubDtos";
import AnimatedTranslationIcon from "../presenters/AnimatedTranslationIcon";
import MessageConfidence from "../presenters/MessageConfidence";
import TripleDotLoading from "../presenters/TripleDotLoading";

export function getAgentColors(theme: GlobalToken): Message["overrideColors"] {
  return {
    background: theme.colorPrimaryBgHover,
    highlight: theme.colorPrimary,
    text: theme.colorText,
  };
}

export function getCustomerColors(theme: GlobalToken): Message["overrideColors"] {
  return {
    background: theme.colorBorder,
    highlight: theme.colorTextPlaceholder,
    text: theme.colorText,
  };
}

export function transcriptItemToMessage(item: TranscriptHistoryItem, theme: GlobalToken, mode: CallTranslatorMode = "translation"): Message<string, Participant> {
  const isFromAgent = item.source.participant === "Agent";
  const isUntranslated = !isFromAgent && item.translatedText === null;
  const messageText = isFromAgent
    ? item.text
    : item.translatedText ?? item.text;

  return {
    key: item.id,
    senderId: item.source.participant,
    children: isUntranslated && mode === "translation"
      ? (
          <TextAndTranslationIconBox>
            <Typography.Text style={{ color: "inherit" }}>{item.text}</Typography.Text>
            <AnimatedTranslationIcon />
          </TextAndTranslationIconBox>
        )
      : messageText,
    extraContent: <MessageConfidence confidence={item.confidence} confidenceThreshold={item.confidenceThreshold} />,
    date: new Date(item.timeStampUtc),
    overrideExtraContentDisplayMode: (item.confidence < item.confidenceThreshold) || isUntranslated
      ? "always"
      : "hover",
    overrideColors: item.source.participant === "Customer" && item.translatedText === null
      ? { ...getCustomerColors(theme), text: theme.colorTextSecondary }
      : undefined,
  };
}

export function intermediateTranscriptToMessage(item: IntermediateTranscriptItem): Message<string, Participant> {
  return {
    key: "intermediate",
    senderId: "Customer",
    children: (
      <Typography.Text style={{ color: "inherit" }}>
        {item.translatedText}
        {" "}
        <TripleDotLoading />
      </Typography.Text>
    ),
    extraContent: null,
    date: new Date(item.timeStampUtc),
  };
}

const TextAndTranslationIconBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${p => p.theme.paddingXXS}px;

  > :nth-child(1) {
    flex: 1 1 auto;
    min-width: 0;
  }

  > :nth-child(2) {
    flex: 0 0 auto;
  }
`;
